import {
  BraidSizes,
  type CTextAlign,
  type BlocksFragment,
  type TabContentFragment,
} from '@seek/cmsu-cms-connect';
import { Box, ContentBlock, Tiles } from 'braid-design-system';
import type React from 'react';

import { BoxWithDecorator } from '../modules';
import { getTileProps } from '../modules/Tiles/helpers';
import { getBoxBackgroundProps, getBoxShadowProps } from '../shared/helper';

import type { AdditionalData } from './AdditionalData';
import { BlockItemRenderer, type BlockItemRenderers } from './BlockItem';

type BlockRendererProps = {
  block: BlocksFragment | TabContentFragment;
  renderers: BlockItemRenderers;
  additionalData: AdditionalData;
  containerBox?: {
    paddingHeight?: BraidSizes | null;
    textAlign?: CTextAlign | null;
  };
  containerIdx?: number;
};
/**
 * BlockRenderer is a component that renders a block.
 * It is used to render a block in the PageComponent.
 * It is also used to render a block in the TabGroup component.
 *
 * @param param0
 * @returns
 */
export const BlockRenderer = ({
  block,
  renderers,
  additionalData,
  containerBox,
  containerIdx = 0,
}: BlockRendererProps) => {
  const paddingProps = block.box?.background
    ? {
        paddingX: {
          mobile: 'medium',
          tablet: block.box?.paddingWidth || 'xlarge',
        },
        paddingY: block.box?.paddingHeight || 'xlarge',
      }
    : {
        paddingX: {
          mobile: 'small',
          tablet: 'small',
          desktop: block.box?.paddingWidth || 'none',
        },
        paddingY: block.box?.paddingHeight || undefined,
      };

  return (
    // If the block is the first block in the first container on the page, use the container's paddingHeight.
    // This enables us set the overall Page paddingY accurately.
    <Box
      key={block.id}
      paddingY={
        containerIdx === 0
          ? containerBox?.paddingHeight || block.box?.paddingHeight || 'small'
          : block.box?.paddingHeight || 'small'
      }
      paddingX={
        block.box?.blockBorder ? { mobile: 'small', desktop: 'none' } : 'none'
      }
    >
      <BoxWithDecorator
        customDecoration={block.box?.customDecoration}
        textAlign={block.box?.textAlign || containerBox?.textAlign || 'left'}
        // Keep the padding to 'none' here when there is no background.
        // Changing it will break alignment of tiles. To add padding to the blocks, adjust the padding of the parent box ^.
        {...(paddingProps as React.ComponentProps<typeof Box>)}
        className="sk-block"
        {...getBoxBackgroundProps(block.box?.background)}
        {...getBoxShadowProps(block.box?.blockBorder)}
        borderRadius={block.box?.borderRadius || 'none'}
      >
        <ContentBlock width={block.box?.width || 'medium'}>
          <Tiles
            {...getTileProps(block.tile)}
            space={block.tile?.space || BraidSizes.Large}
          >
            {block.items.map((item, idx) => (
              <Box
                background={block.tile?.background || undefined}
                borderRadius={block.tile ? 'large' : 'none'}
                height="full"
                key={idx}
                className="sk-block-item"
              >
                <BlockItemRenderer
                  block={item}
                  renderers={renderers}
                  additionalData={additionalData}
                />
              </Box>
            ))}
          </Tiles>
        </ContentBlock>
      </BoxWithDecorator>
    </Box>
  );
};
